(ns client-portal.client.ai-agent.chat.views
  "Chat conversation UI shared by the full-page agent and the report-chat side panel.
   Depends only on the chat data ns (and generic shared components/charts) — never on
   client.reports — so reports can open this panel without a dependency cycle. Report
   rendering stays in client.ai-agent.views, which may depend on client.reports: the
   report-builder's own view, and the components an agent message carries (the
   data-question agent), which reach this ns through
   `chat.d/message-components-view*`."
  (:require [client-portal.client.ai-agent.chat.data :as chat.d]
            [client-portal.client.ai-agent.guide :as guide]
            [client-portal.client.products.views :as products.v]
            [client-portal.client.voice-insights.charts.views :as charts]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.components.components :as c]
            [client-portal.client.layout :as l]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]
            ["react-markdown" :default ReactMarkdown]
            ["remark-gfm" :default remarkGfm]))

(defn send-chat-buttons []
  (let [val- (re/subscribe [:ai-agent/input-value-])
        resp- (re/subscribe [:ai-agent/message-response-])
        pending-msg- (re/subscribe [:ai-agent/pending-message-])
        ;; Pending covers the gap between sending and the request actually going out
        ;; (an auth0 token round-trip), where nothing is loading yet but the answer is
        ;; already coming.
        in-flight? (or (util/loading? @resp-) (not-empty @pending-msg-))]
    [:<>
     ;; Stop sits beside the send button rather than replacing it — the composer is
     ;; always in reach, unlike the thinking indicator, which scrolls with the chat.
     (when in-flight?
       [alc/up-call-to-action-button {:icon-type :StopIcon
                                      :tooltip-content "Stop"
                                      :style {:background-color (style/alc-color "Common/error")}
                                      :on-click #(re/dispatch [:ai-agent/stop-response])}])
     [alc/up-call-to-action-button {:icon-type :SendIcon
                                    :disabled (or (empty? @val-) in-flight?)
                                    :tooltip-content "Send"
                                    :on-click #(re/dispatch [:ai-agent/send-chat])}]]))

(defn product-switcher
  "Product the report is authored against, shown on the textarea's action row by the
   views that opt in — and only when the tenant has more than one product."
  []
  (let [products- (re/subscribe [:products/list-])
        product- (re/subscribe [:ai-agent/report-product-])]
    (fn []
      (when (> (count (util/when-not-loading @products-)) 1)
        [products.v/products-dropdown {:value @product-
                                       :only-reference-ids #{"oasys" "voice-insights"}
                                       :on-change #(re/dispatch [:ai-agent/select-report-product %])}]))))

(defn chat-textarea [_]
  (let [resp- (re/subscribe [:ai-agent/message-response-])
        pending-msg- (re/subscribe [:ai-agent/pending-message-])
        report-builder?- (re/subscribe [:ai-agent/report-builder-agent?-])
        report-chat?- (re/subscribe [:ai-agent/report-chat?-])
        agent-name- (re/subscribe [:ai-agent/display-name-])
        set-dom-el-size (fn [this]
                          (let [rect (-> (re/dom-node this)
                                         (.getBoundingClientRect))]
                            (re/dispatch [:ai-agent/set-chat-textarea-size {:height (.-height rect)
                                                                            :width (.-width rect)}])))]
    (re/create-class
      {:component-did-mount set-dom-el-size
       :component-did-update set-dom-el-size
       :reagent-render
       (fn [{:keys [placeholder container-style textarea-style product-switcher?]}]
         [:div {:style (merge {:width "100%"}
                              container-style)}
          [:div {:style {:position :relative
                         :flex "0 1 auto"
                         :max-height "min(100%, 400px)"}}
           [alc/up-textarea {:class-name style/hidden-scrollbar-class-name
                             :auto-focus true
                             :form-path chat.d/root-path
                             :form-name :input
                             :placeholder (or placeholder
                                              (cond
                                                @report-builder?- "What would you like to see in your report?"
                                                @report-chat?- "What would you like to know about this report?"
                                                :else "What questions do you have?"))
                             :on-key-down #(when (and (not (util/loading? @resp-))
                                                      (empty? @pending-msg-)
                                                      (util/enter-key-event? %)
                                                      (not (.-shiftKey %)))
                                             (re/dispatch [:ai-agent/send-chat])
                                             (util/stop-event %))
                             :container-style {:height "100%"}
                             :style (merge {:background-color (style/alc-color "Background/page")
                                            :padding "20px 20px 52px"
                                            :resize :none
                                            :field-sizing :content
                                            :min-height 128
                                            :max-height 400
                                            :max-width "100%"}
                                           textarea-style)}]
           [:div {:style {:background-color (style/alc-color "Background/page")
                          :border-radius "0 0 6px 6px"
                          :display :flex
                          :align-items :center
                          :gap 8
                          :margin-top 4
                          :padding 8
                          :position :absolute
                          :bottom 1
                          :left 1
                          :height 52
                          :width "calc(100% - 2px)"
                          :z-index 1}}
            (when product-switcher?
              [product-switcher])
            [c/spacer]
            [send-chat-buttons]]]
          [:div {:style {:background-color (style/alc-color "Background/card_hex")
                         :color (style/alc-color "Text/primary")
                         :font-size 12
                         :font-weight 400
                         :text-align :center
                         :padding "6px 0"}}
           (str @agent-name- " can make mistakes. Verify important figures against the source data.")]])})))

(defn message-action [_]
  (let [icon-type-override* (re/ratom nil)]
    (fn [{:keys [icon-type tooltip-content on-click-icon-type on-click disabled?]}]
      [alc/tooltip
       {:tooltip-content tooltip-content
        :target-content [alc/up-ghost-button {:icon-type (or @icon-type-override* icon-type)
                                              :disabled disabled?
                                              :on-click (when-not disabled?
                                                          #(do
                                                             (on-click %)
                                                             (when on-click-icon-type
                                                               (reset! icon-type-override* on-click-icon-type)
                                                               (util/wait 2000 (fn [] (reset! icon-type-override* nil))))))}]}])))

(defn user-message [_]
  (let [hover?* (re/ratom false)
        resp- (re/subscribe [:ai-agent/message-response-])]
    (fn [{:keys [id content stopped?]}]
      [c/interactive
       :div {:id (str "user-message-" id)
             :data-user-message true
             :style {:display :flex
                     :align-items :center
                     :justify-content :flex-end
                     :position :relative
                     :padding "30px 0"
                     :width "100%"}
             :hover?* hover?*}
       [:div {:style {:display :flex
                      :flex-direction :column
                      :align-items :flex-end
                      :gap 4
                      :max-width "90%"}}
        ;; A question whose answer was stopped reads as muted and unanswered rather
        ;; than as one the agent simply never replied to.
        [:div {:style {:background-color (style/alc-color "Common/active")
                       :border-radius 6
                       :color (style/alc-color "Common/White")
                       :font-size 16
                       :line-height 1.2
                       :opacity (if stopped? 0.4 1)
                       :padding "12px 16px"
                       :max-width "100%"}}
         [:> ReactMarkdown
          {:children (or content "⚠️ Oops, something must've gone wrong. Please try again.")
           :remarkPlugins [remarkGfm]}]]
        (when stopped?
          [:span {:style {:color (style/alc-color "Text/secondary")
                          :font-size 12
                          :line-height 1.33}}
           "Stopped"])]
       [:div {:style {:display :flex
                      :align-items :center
                      :justify-content :flex-end
                      :gap 3
                      :opacity (if @hover?* 1 0)
                      :padding-top 5
                      :position :absolute
                      :top "calc(100% - 25px)"
                      :right 0
                      :width "100%"
                      :z-index 1}}
        [message-action {:icon-type :CopyIcon
                         :on-click-icon-type :CheckCircleIcon
                         :on-click #(re/dispatch-sync [:core/copy-to-clipboard content])
                         :tooltip-content "Copy"}]
        [message-action {:icon-type :RenewIcon
                         :disabled? (util/loading? @resp-)
                         :on-click #(re/dispatch-sync [:ai-agent/send-chat {:content-override content}])
                         :tooltip-content "Retry"}]]])))

(defn custom-code-block [_]
  (let [hover?* (re/ratom false)
        admin?- (re/subscribe [:account/admin-role?-])]
    (fn [props]
      (let [{:keys [title description] :as chart-config} (-> props
                                                             :children
                                                             (util/json-string->map-basic)
                                                             (util/->kebab-case-keyword-top-level))]
        (if (= (:className props) "language-recharts")
          [c/interactive
           :div {:style {:position :relative
                         :width "100%"}
                 :hover?* hover?*}
           (when @hover?*
             [:div {:style {:display :flex
                            :align-items :center
                            :gap 8
                            :position :absolute
                            :top -5
                            :right -5
                            :z-index 2}}
              (when @admin?-
                [alc/tooltip
                 {:target-content [alc/purple-button {:material-icon-type :dataset
                                                      :on-click #(re/dispatch-sync [:core/copy-to-clipboard
                                                                                    (:data chart-config)
                                                                                    {:notify? true}])}]
                  :tooltip-content "Copy data"}])
              (when @admin?-
                [alc/tooltip
                 {:target-content [alc/purple-button {:material-icon-type :area-chart
                                                      :on-click #(re/dispatch-sync [:core/copy-to-clipboard
                                                                                    (-> chart-config
                                                                                        (dissoc :query :params :data)
                                                                                        (pr-str))
                                                                                    {:notify? true}])}]
                  :tooltip-content "Copy chart config"}])
              (when @admin?-
                [alc/tooltip
                 {:target-content [alc/purple-button {:material-icon-type :content-copy
                                                      :on-click #(re/dispatch-sync [:core/copy-to-clipboard
                                                                                    (-> chart-config
                                                                                        (select-keys [:query :params])
                                                                                        (util/map->json-string))
                                                                                    {:notify? true}])}]
                  :tooltip-content "Copy query and params"}])
              #_[alc/purple-button {:on-click #()}
                 "Save to Report"]])
           (when (or (not-empty title) (not-empty description))
             [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 10
                            :margin-bottom 20
                            :white-space :normal}}
              (when (not-empty title)
                [:span {:style {:color (style/alc-color "Text/primary_header")
                                :font-size 14
                                :font-weight 400
                                :line-height 1.43}}
                 title])
              (when (not-empty description)
                [:span {:style {:color (style/alc-color "Text/secondary")
                                :font-size 12
                                :font-weight 400
                                :line-height 1.33}}
                 description])])
           [charts/chart-from-config chart-config]]
          (if (:inline props)
            [:code
             (dissoc props :node :children)
             (:children props)]
            [:div
             [:code
              (dissoc props :node :children)
              (:children props)]]))))))

(defn agent-message [_]
  (let [hover?* (re/ratom false)
        resp- (re/subscribe [:ai-agent/message-response-])]
    (fn [{:keys [id content components last? idx messages]}]
      (let [retry-message (when last?
                            (try
                              (nth messages (dec idx))
                              (catch :default _
                                nil)))
            components-view (when (not-empty components) @chat.d/message-components-view*)]
        [c/interactive
         :div {:class-name "ai-agent-agent-message"
               :data-agent-message true
               :style {:display :flex
                       :align-items :center
                       :justify-content :flex-end
                       :position :relative
                       :padding "30px 0"
                       :width "100%"}
               :hover?* hover?*}
         [:div {:class-name "markdown-body"
                :style {:color (style/alc-color "Text/primary")
                        :font-size 16
                        :line-height 1.4
                        :width "100%"}}
          [:> ReactMarkdown
           {:children (or content "⚠️ Oops, something must've gone wrong. Please try again.")
            :remarkPlugins [remarkGfm]
            :components (clj->js {"code" (re/reactify-component custom-code-block)})}]
          (when components-view
            [:div {:style {:display :flex
                           :margin-top 16
                           :width "100%"}}
             [components-view {:components components
                               :report-id id}]])
          [:div {:style {:display :flex
                         :align-items :center
                         :justify-content :flex-end
                         :gap 3
                         :opacity (if (or last? @hover?*) 1 0)
                         :position :absolute
                         :top "calc(100% - 26px)"
                         :right 0
                         :width "100%"
                         :z-index 1}}
           [message-action {:icon-type :CopyIcon
                            :on-click-icon-type :CheckCircleIcon
                            :on-click #(re/dispatch-sync [:core/copy-to-clipboard content])
                            :tooltip-content "Copy"}]
           (when retry-message
             [message-action {:icon-type :RenewIcon
                              :disabled? (util/loading? @resp-)
                              :on-click #(re/dispatch-sync [:ai-agent/send-chat
                                                            {:content-override (:content retry-message)}])
                              :tooltip-content "Retry"}])]]]))))

(defn with-bottom-chat-textarea [_]
  (fn [{:keys [container-id container-style bottom-container-style side-padding placeholder
               above-textarea-content product-switcher?]
        :or {side-padding 90}}
       & contents]
    (into [:div {:id container-id
                 :style (merge {:display :flex
                                :flex-direction :column
                                :align-items :center
                                :padding (str "40px " side-padding "px 0")}
                               container-style)}]
          (concat contents
                  [[:div {:style (merge {:display :flex
                                         :align-items :center
                                         :justify-content :center
                                         :position :sticky
                                         :bottom 0
                                         :width "100%"
                                         :max-width 720
                                         :z-index 10}
                                        bottom-container-style)}
                    ;; overlay above the sticky textarea so it stays pinned to the bottom of
                    ;; the scrollport (e.g. the scroll-to-bottom button) in any layout
                    (when above-textarea-content
                      [:div {:style {:position :absolute
                                     :bottom "100%"
                                     :left 0
                                     :display :flex
                                     :justify-content :center
                                     :padding-bottom 10
                                     :width "100%"
                                     :z-index 4}}
                       above-textarea-content])
                    [chat-textarea {:id :ai-agent-chat-textarea
                                    :placeholder placeholder
                                    :product-switcher? product-switcher?
                                    :textarea-style {:width "100%"}}]]]))))

(defn get-last-user-message []
  (some-> (js/document.querySelectorAll "[data-user-message]")
          (js/Array.prototype.slice.call)
          (.at -1)))

(defn chat-scroll-container [node]
  ;; full-page the chat scrolls in an ancestor (#core-scroll-content); in the right
  ;; panel the chat container is its own scroll container — resolve either here
  (when node
    (let [overflow-y (-> node js/window.getComputedStyle .-overflowY)]
      (if (contains? #{"auto" "scroll" "overlay"} overflow-y)
        node
        (util/find-scroll-container node)))))

(defn session-chat [_]
  (let [spacer-height* (re/ratom 0)
        scroll-position- (re/subscribe [:core/layout-content-container-scroll-position-])
        pending-msg- (re/subscribe [:ai-agent/pending-message-])
        vs- (re/subscribe [:core/viewport-size])
        chat-textarea-size- (re/subscribe [:ai-agent/chat-textarea-size-])
        update-spacer! (fn [this]
                         (let [{:keys [messages extra-spacer-height]} (re/props this)
                               container (re/dom-node this)]
                           (when container
                             (let [container-styles (js/window.getComputedStyle container)
                                   container-top (some-> (chat-scroll-container container)
                                                         (.getBoundingClientRect)
                                                         (.-top))
                                   padding-top (some-> container-styles (.-paddingTop) (js/parseFloat))
                                   padding-bottom (some-> container-styles (.-paddingBottom) (js/parseFloat))
                                   last-user-message (get-last-user-message)
                                   last-message-speaker (-> messages (last) :speaker)]
                               (reset! spacer-height* (max 30 (- js/window.innerHeight
                                                                 container-top
                                                                 padding-top
                                                                 padding-bottom
                                                                 (:height @chat-textarea-size-)
                                                                 extra-spacer-height
                                                                 20
                                                                 (when (= last-message-speaker "agent")
                                                                   (some-> (js/document.querySelectorAll "[data-agent-message]")
                                                                           (js/Array.prototype.slice.call)
                                                                           (.at -1)
                                                                           (.-clientHeight)))
                                                                 (some-> last-user-message (.-clientHeight)))))))))
        track-scroll! (fn [this]
                        (when-let [scroll-container (some-> (re/dom-node this) (chat-scroll-container))]
                          (when-not (.-onscroll scroll-container)
                            (set! (.-onscroll scroll-container)
                                  (fn [e]
                                    (re/dispatch [:core/set-layout-content-container-scroll-position
                                                  (.. e -target -scrollTop)]))))))
        on-change! (fn [this]
                     (update-spacer! this)
                     (track-scroll! this))]
    (re/create-class
      {:component-did-mount
       on-change!
       :component-did-update
       on-change!
       :reagent-render
       (fn [{:keys [messages container-id container-style chat-container-style bottom-container-style
                    side-padding product-switcher?]}]
         @vs- @chat-textarea-size- @scroll-position-
         (let [last-user-message (get-last-user-message)
               last-user-message-visibility (when last-user-message
                                              (util/element-visibility-info last-user-message))
               scroll-to-bottom? (and last-user-message
                                      (not (:visible? last-user-message-visibility))
                                      (>= (-> last-user-message-visibility :offsets :top) 0))]
           [with-bottom-chat-textarea {:container-id container-id
                                       :placeholder "Anything else?"
                                       :container-style container-style
                                       :bottom-container-style bottom-container-style
                                       :side-padding side-padding
                                       :product-switcher? product-switcher?
                                       :above-textarea-content (when scroll-to-bottom?
                                                                 [alc/tooltip
                                                                  {:target-content [alc/up-call-to-action-button {:icon-type :ArrowDownIcon
                                                                                                                  :on-click #(re/dispatch [:ai-agent/scroll-to-bottom-of-messages])}]
                                                                   :tooltip-content "Scroll To Bottom"}])}
            (into [:div {:style (merge {:flex 1
                                        :padding "0 10px"
                                        :width "100%"
                                        :max-width 720}
                                       chat-container-style)}]
                  (concat
                    (map-indexed (fn [idx {:keys [speaker] :as msg}]
                                   (if (= speaker "user")
                                     [user-message msg]
                                     [agent-message (assoc msg :last? (= idx (dec (count messages)))
                                                               :idx idx
                                                               :messages messages)]))
                                 messages)
                    [[:div {:style {:height @spacer-height*}}
                      (when (and (some-> @spacer-height* (pos?)) @pending-msg-)
                        [alc/up-loader-with-label {:label "Thinking..."
                                                   :loader-size "medium"
                                                   :style {:flex "0 0 auto"
                                                           :min-height 0
                                                           :padding "25px 0 10px"}}])]]))]))})))

(defn ai-agent-suggestion [{:keys [text]}]
  (let [gray-border "1px solid rgba(0, 0, 0, 0.2)"]
    [c/interactive
     :div {:style {:background-color (util/hex->rgba (style/alc-color "Common/active") 0.3)
                   :border-top gray-border
                   :border-right gray-border
                   :border-bottom gray-border
                   :border-left (str "3px solid " (style/alc-color "Common/active"))
                   :border-radius 6
                   :color (style/alc-color "Text/primary")
                   :font-size 16
                   :line-height 1.2
                   :cursor :pointer
                   :padding "12px 16px"
                   :width "calc(50% - 5px)"
                   :transition "0.2s"}
           :hover-style {:box-shadow "0 2px 3px 0 rgba(34,36,38,.15)"}
           :on-click #(re/dispatch [:ai-agent/send-chat {:content-override text}])}
     text]))

(defn new-chat [{:keys [container-id container-style bottom-container-style side-padding product-switcher?]}]
  (let [menu-items?- (re/subscribe [:voice-insights/menu-items-feature-enabled?-])
        ces?- (re/subscribe [:voice-insights/ces-feature-enabled?-])
        report-builder?- (re/subscribe [:ai-agent/report-builder-agent?-])
        report-chat?- (re/subscribe [:ai-agent/report-chat?-])
        agent-name- (re/subscribe [:ai-agent/display-name-])]
    [with-bottom-chat-textarea (cond-> {:container-style (merge {:flex 1
                                                                 :height "100%"}
                                                                container-style)
                                        :bottom-container-style bottom-container-style
                                        :product-switcher? product-switcher?}
                                       container-id (assoc :container-id container-id)
                                       side-padding (assoc :side-padding side-padding))
     [:div {:style {:flex 1
                    :display :flex
                    :flex-direction :column
                    :align-items :center
                    :justify-content :center
                    :gap 10
                    :margin-top 40
                    :padding "0 10px"
                    :height "100%"
                    :width "100%"
                    :max-width 720}}
      [:span {:style {:flex "0 0 auto"
                      :color (style/alc-color "Text/primary")
                      :font-size 28
                      :font-weight 700
                      :line-height 1.2
                      :text-align :center}}
       (cond
         @report-builder?- "Build Your Report"
         @report-chat?- "Ask Anything About Your Report Data"
         :else "Ask Anything About Your Voice Data")]
      [:div {:style {:flex "0 0 auto"
                     :color (style/alc-color "Text/secondary")
                     :font-size 15
                     :font-weight 400
                     :line-height 1.4
                     :text-align :center
                     :margin-bottom 10}}
       (cond
         @report-builder?- "Report Builder will allow you to build reports via prompting"
         @report-chat?- "Ask about the metrics, trends, and takeaways in this report"
         :else (str @agent-name- " analyzes your "
                    (->> ["sessions"
                          (when @menu-items?-
                            "menu items, upsell performance, availability issues")
                          (when @ces?-
                            "customer effort scores")
                          "and wait times"]
                         (filter identity)
                         (s/join ", "))
                    " across all of your locations. Try a starter:"))]
      (when-not (or @report-builder?- @report-chat?-)
        (into [:div {:style {:display :flex
                             :flex-wrap :wrap
                             :justify-content :space-between
                             :gap 10
                             :margin-top 10}}]
              (mapv (fn [text]
                      [ai-agent-suggestion {:text text}])
                    ["What are the top three things I should work on operationally?"
                     "Which menu items are most often misheard?"
                     "Compare wait times across my top 5 locations this week?"
                     "Where is upsell conversion underperforming?"])))]]))

(defn chat-error []
  [:div {:style {:display :flex
                 :flex-direction :column
                 :align-items :center
                 :justify-content :flex-start
                 :gap 24
                 :padding "128px 24px 24px 24px"
                 :width "100%"
                 :min-height "100%"
                 :flex 1}}
   [:div {:style {:color (style/alc-color "Text/primary")}}
    [:> ReactMarkdown
     {:children "⚠️ Uh oh, the attempt to load your chat session failed."
      :remarkPlugins [remarkGfm]}]]
   [:div {:style {:display :flex
                  :gap 10}}
    [alc/up-button {:on-click #(re/dispatch [:ai-agent/start-new-chat])}
     "Start New Chart"]
    [alc/up-call-to-action-button {:on-click #(re/dispatch [:ai-agent/load-session])}
     "Try Again"]]])

(defn chat-content [_]
  (let [session- (re/subscribe [:ai-agent/session-])
        loading?- (re/subscribe [:ai-agent/session-loading?-])
        messages- (re/subscribe [:ai-agent/session-messages-])]
    (fn [args]
      (cond
        (:error-code @session-)
        [chat-error]

        @loading?-
        [:div {:style {:display :flex
                       :padding "12px 0"
                       :width "100%"
                       :min-height "100%"}}
         [empty-state/loading-card {:label "Loading Chat..."}]]

        (not-empty @messages-)
        [:div {:style {:position :relative
                       :min-height "100%"
                       :height "100%"}}
         [session-chat (merge {:messages @messages-}
                              args)]]

        :else
        [new-chat args]))))

(defn report-builder-panel []
  (let [panel-view* (re/ratom "chat")
        cur-route- (re/subscribe [:router/current-route-])]
    (fn []
      [:div {:style {:display :flex
                     :flex-direction :column
                     :height "100%"}}
       ;; The flanking flex-1 sides keep the toggle group centered on the header
       ;; row while the New Report button sits at its right edge.
       [:div {:style {:flex "0 0 auto"
                      :align-items :center
                      :display :flex
                      :padding "12px 16px 6px"}}
        [c/spacer]
        [alc/up-toggle-group {:type "single"
                              :value @panel-view*
                              :on-value-change (fn [v]
                                                 (when (seq v)
                                                   (reset! panel-view* v)))
                              :items [{:value "chat" :content "Chat"}
                                      {:value "guide" :content "Guide"}]}]
        [:div {:style {:display :flex
                       :flex 1
                       :justify-content :flex-end}}
         ;; The blank builder route already is a new report.
         (when (not= (first @cur-route-) :reports/new)
           [alc/tooltip
            {:target-content [alc/up-button {:icon-type :AddIcon
                                             :rounded? true
                                             :on-click #(re/dispatch [:ai-agent/start-new-report])}]
             :tooltip-content "New Report"}])]]
       [:div {:id chat.d/chat-panel-scroll-container-id
              :style {:flex 1
                      :overflow :auto
                      :scrollbar-gutter "stable both-edges"}}
        (if (= @panel-view* "guide")
          [guide/guide-content]
          [chat-content {:container-style {:min-height "100%"}
                         :side-padding 20
                         :extra-spacer-height -30
                         :product-switcher? true}])]])))

(defmethod l/panel-right-view
  :ai-agent/chat-panel [_]
  [report-builder-panel])

(defmethod l/panel-right-view
  :ai-agent/report-chat-panel [_]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :height "100%"}}
   [:div {:style {:background-color (style/alc-color "Background/secondary")
                  :flex "0 0 auto"
                  :display :flex
                  :align-items :center
                  :gap 8
                  :padding "12px 16px"
                  :min-height 58
                  :box-sizing :border-box
                  :z-index (inc (style/z :medium))}}
    [:span {:style {:color (style/alc-color "Text/primary_header")
                    :display :flex
                    :align-items :center
                    :font-size 20
                    :font-weight 400
                    :line-height 1.4}}
     "Ask a Question"]
    [c/spacer]
    [alc/tooltip
     {:target-content [alc/up-button {:icon-type :AddIcon
                                      :rounded? true
                                      :on-click #(re/dispatch [:ai-agent/start-new-chat])}]
      :tooltip-content "New Chat"}]
    [alc/up-button {:icon-type :CrossIcon
                    :rounded? true
                    :on-click #(re/dispatch [:ai-agent/close-report-chat])}]]
   [:div {:style {:flex 1
                  :overflow :auto
                  :scrollbar-gutter "stable both-edges"}}
    [chat-content {:container-style {:min-height "100%"}
                   :side-padding 20
                   :extra-spacer-height -30}]]])
